import { NgbDate, NgbCalendar, NgbDateStruct, NgbDatepicker, NgbDateParserFormatter, NgbDateAdapter, NgbDatepickerNavigateEvent } from '@ng-bootstrap/ng-bootstrap'
import { Component, EventEmitter, Injectable, Input, Output, inject } from '@angular/core'


@Injectable()
export class USDateAdapter extends NgbDateAdapter<string> {
	readonly DELIMITER = '/'

	fromModel(value: string | null): NgbDateStruct | null {
		if (value) {
			const date = value.split(this.DELIMITER)
			return {
				day: parseInt(date[1], 10),
				month: parseInt(date[0], 10),
				year: parseInt(date[2], 10),
			}
		}
		return null
	}

	toModel(date: NgbDateStruct | null): string | null {
		return date ? date.month + this.DELIMITER + date.day + this.DELIMITER + date.year : null
	}
}

@Injectable()
export class USDateParserFormatter extends NgbDateParserFormatter {
	readonly DELIMITER = '/'

	parse(value: string): NgbDateStruct | null {
		if (value) {
			const date = value.split(this.DELIMITER)
			const year = parseInt(date[2], 10)

			if (year > 1900) {
				return {
					day: parseInt(date[1], 10),
					month: parseInt(date[0], 10),
					year,
				}
			}
		}
		return null
	}

	format(date: NgbDateStruct | null): string {
		return date ? date.month + this.DELIMITER + date.day + this.DELIMITER +  date.year : ''
	}
}

@Component({
    selector: 'date-range',
    templateUrl: './date-range.component.html',
    styleUrls: ['./date-range.component.scss'],
    imports: [NgbDatepicker],
    providers: [
        { provide: NgbDateAdapter, useClass: USDateAdapter },
        { provide: NgbDateParserFormatter, useClass: USDateParserFormatter },
    ]
})
export class DateRangeComponent {

	calendar = inject(NgbCalendar)
	formatter = inject(NgbDateParserFormatter)


	@Input() set minDate(minDate: Date) {
		this.fromDate = this.dateToNgbDate(minDate)
	}
	@Output() minDateChange = new EventEmitter<Date>()

	@Input() set maxDate(maxDate: Date) {
		this.toDate = this.dateToNgbDate(maxDate)
	}
	@Output() maxDateChange = new EventEmitter<Date>()

	public startDate: NgbDate
	public hoveredDate: NgbDate | null = null
	public fromDate: NgbDate | null = null
	public toDate: NgbDate | null = null

	public calendarMinDate = {
		day: 1,
		month: 1,
		year: 1997,
	}

	constructor() {
		const today = this.calendar.getToday()
		this.startDate = this.calendar.getPrev(today, 'm', 1)
		this.fromDate = today
		this.toDate = today
	}

	onDateSelection(date: NgbDate) {
		if (!this.fromDate && !this.toDate) {
			this.fromDate = date
			this.minDateChange.emit(this.NgbDateToDate(date))
		} else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
			this.toDate = date
			this.maxDateChange.emit(this.NgbDateToDate(date))
		} else {
			this.toDate = null
			this.fromDate = date
			this.minDateChange.emit(this.NgbDateToDate(date))
		}
	}

	onFromNavigateEvent(event: NgbDatepickerNavigateEvent) {
		if (event.current) {
			this.fromDate = new NgbDate(event.next.year, event.next.month, this.fromDate!.day)
			this.minDateChange.emit(this.NgbDateToDate(this.fromDate))
		}
	}

	onToNavigateEvent(event: NgbDatepickerNavigateEvent) {
		if (event.current) {
			this.toDate = new NgbDate(event.next.year, event.next.month, this.toDate!.day)
			this.maxDateChange.emit(this.NgbDateToDate(this.toDate))
		}
	}

	isHovered(date: NgbDate) {
		return (
			this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate)
		)
	}

	isInside(date: NgbDate) {
		return this.toDate && date.after(this.fromDate) && date.before(this.toDate)
	}

	isRange(date: NgbDate) {
		return (
			date.equals(this.fromDate) ||
			(this.toDate && date.equals(this.toDate)) ||
			this.isInside(date) ||
			this.isHovered(date)
		)
	}

	dateToNgbDate(d: Date): NgbDate {
		return new NgbDate(d.getFullYear(), d.getMonth() + 1, d.getDate())
	}

	NgbDateToDate(d: NgbDate): Date {
		return new Date(d.year, d.month - 1, d.day)
	}

	validateInput(currentValue: NgbDate | null, input: string, emitter: EventEmitter<Date>): NgbDate | null {
		const parsed = this.formatter.parse(input)
		const parsedDate = NgbDate.from(parsed)
		if (parsed && this.calendar.isValid(parsedDate)) {
			emitter.emit(this.NgbDateToDate(parsedDate!))
			return parsedDate
		} else {
			return null
		}
	}
}
